import React, { useEffect } from 'react'
import { useIsFocused, useNavigation } from '@react-navigation/native'
import { useDispatch, useSelector } from 'react-redux'
import { SafeAreaView } from 'react-native-safe-area-context'
import { StyleService, useStyleSheet } from '@src/style/service'
import { useSnack } from '@src/utils'
import { ErrorMessages } from '@src/config'
import { HealthDataMetricBiomarker, HealthMetric } from '@src/types'
import { ScrollViewWithFade } from '@src/components'
import { Button } from '@src/components/base/Button'
import { useSecureScreen } from '@src/hooks/useSecureScreen'
import { healthMetricsSelector } from '../models/labResults.selectors'
import { BiomarkerCard } from '../components/BiomarkerCard'
import { NoLabResultsProvidedComponent } from '../components/NoLabResultsProvidedComponent'
import { LabResultsNavigationContainer } from '../components/LabResultsNavigationContainer'

export const BiomarkersScreen = () => {
  useSecureScreen()

  const styles = useStyleSheet(themedStyles)
  const healthMetrics = useSelector(healthMetricsSelector)
  const isFocused = useIsFocused()
  const showSnack = useSnack()
  const dispatch = useDispatch()
  const navigation = useNavigation()

  const availableBiomarkerKinds = new Set<HealthDataMetricBiomarker>(
    healthMetrics?.healthMetrics.map((metric: HealthMetric) => metric.kind) || [],
  )

  const biomarkerGroups = Array.from(availableBiomarkerKinds).flatMap((kind) => {
    // Gather all biomarkers of this kind
    const biomarkerData =
      healthMetrics?.healthMetrics.filter(
        (healthMetric: HealthMetric) => healthMetric.kind === kind,
      ) || []

    if (biomarkerData.length === 0) {
      return []
    }

    return {
      data: biomarkerData,
      title: biomarkerData[0].label,
      unit: biomarkerData[0].unit || undefined,
      metricKey: biomarkerData[0].kind,
    }
  })

  useEffect(() => {
    if (!isFocused) {
      return
    }

    dispatch({
      type: 'labResults/fetchHealthMetrics',
      failure: (error: any) => {
        const errorMessage = error?.message || ErrorMessages.ServerError
        showSnack(errorMessage, null, 'error')
      },
    })
  }, [dispatch, showSnack, isFocused])

  const noDataAvailable = biomarkerGroups.length === 0

  if (noDataAvailable) {
    return (
      <LabResultsNavigationContainer screen="BiomarkersScreen">
        <NoLabResultsProvidedComponent />
      </LabResultsNavigationContainer>
    )
  }

  return (
    <LabResultsNavigationContainer screen="BiomarkersScreen">
      <SafeAreaView style={styles.container} edges={['bottom']}>
        <ScrollViewWithFade>
          {biomarkerGroups.map((biomarker) => (
            <BiomarkerCard key={biomarker.metricKey} {...biomarker} />
          ))}
        </ScrollViewWithFade>
        <Button
          accessibilityLabel="Upload new lab results"
          onPress={() => navigation.navigate('AddLabResults')}
          style={styles.uploadButton}
          size="block"
          type="primary"
        >
          Add Lab Results
        </Button>
      </SafeAreaView>
    </LabResultsNavigationContainer>
  )
}

const themedStyles = StyleService.create({
  container: {
    flex: 1,
    paddingTop: 8,
    paddingHorizontal: 16,
  },
  uploadButton: {
    marginBottom: 8,
  },
})
